// Top-level app — boots auth, then mounts the shell with the active view.
//
// Routing is query-string based per spec §10.3:
//   ?view=home      (default)
//   ?view=reorder
//   ?view=designs   (?folder=, ?search=, ?design= sub-routes)
//   ?view=orders    (?order=)
//   ?view=kit
//   ?view=team
//   ?view=help
//
// Why query strings instead of paths: this SPA mounts on Shopify Liquid
// pages (/pages/account, /pages/my-designs) that already have their own
// path. Sub-paths would 404 against Shopify's routing; query strings
// don't.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CustomerSummary } from './lib/types';
import { bootstrapAuth, type AuthState } from './lib/auth';
import { useStudio, type ViewName } from './lib/store';
import { AppShell } from './components/Shell/AppShell';
import { HomeView } from './views/Home/HomeView';
import { QuickReorderView } from './views/QuickReorder/QuickReorderView';
import { MyDesignsView } from './views/MyDesigns/MyDesignsView';
import { OrdersView } from './views/Orders/OrdersView';
import { BrandKitView } from './views/BrandKit/BrandKitView';
import { MyTeamView } from './views/MyTeam/MyTeamView';
import { HelpView } from './views/Help/HelpView';
import { AuthGate } from './components/AuthGate';
import { ToastDeck } from './components/Toast';

const VIEW_NAMES: ReadonlySet<ViewName> = new Set([
  'home',
  'reorder',
  'designs',
  'orders',
  'kit',
  'team',
  'help',
]);

function readViewFromUrl(searchParams: URLSearchParams): ViewName {
  const raw = searchParams.get('view');
  if (raw && VIEW_NAMES.has(raw as ViewName)) return raw as ViewName;
  // Special case: when the page itself is /pages/my-designs, default to
  // the My Designs view rather than Home, so the URL feels natural.
  if (typeof window !== 'undefined' && /\/pages\/my-designs/i.test(window.location.pathname)) {
    return 'designs';
  }
  return 'home';
}

export function App() {
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' });
  const view = useStudio((s) => s.view);
  const setView = useStudio((s) => s.setView);
  const [searchParams, setSearchParams] = useSearchParams();

  // Boot auth once.
  useEffect(() => {
    let cancelled = false;
    bootstrapAuth().then((next) => {
      if (!cancelled) setAuth(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Sync URL ↔ store. URL is the source of truth on first paint and when
  // the user clicks browser back/forward; the store is the source of truth
  // when sidebar nav is clicked.
  useEffect(() => {
    setView(readViewFromUrl(searchParams));
  }, [searchParams, setView]);

  function navigate(next: ViewName) {
    const params = new URLSearchParams(searchParams);
    params.set('view', next);
    setSearchParams(params);
    setView(next);
  }

  if (auth.status === 'loading') {
    return <BootSplash />;
  }
  if (auth.status === 'unauthenticated') {
    return (
      <AuthGate
        onAuthenticated={(c) =>
          setAuth({ status: 'authenticated', customer: c, staffActingAs: null })
        }
      />
    );
  }

  return (
    <>
      {auth.staffActingAs && <StaffBanner adminEmail={auth.staffActingAs.email} customerEmail={auth.customer.email} />}
      <AppShell customer={auth.customer} view={view} onNavigate={navigate}>
        {renderView(view, auth.customer)}
      </AppShell>
      <ToastDeck />
    </>
  );
}

function StaffBanner({
  adminEmail,
  customerEmail,
}: {
  adminEmail: string;
  customerEmail: string;
}) {
  return (
    <div
      role="alert"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 9999,
        background: '#b22c2c',
        color: '#fff',
        padding: '8px 16px',
        fontSize: 13,
        fontWeight: 600,
        textAlign: 'center',
        display: 'flex',
        gap: 12,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <span>
        Staff mode — {adminEmail} viewing as {customerEmail}. Cart actions are disabled.
      </span>
      <button
        type="button"
        onClick={async () => {
          try {
            const { exitStaffMode } = await import('./lib/api');
            await exitStaffMode();
          } catch {
            // best-effort; reload anyway so the cookie clears via client side
          }
          window.location.reload();
        }}
        style={{
          background: '#fff',
          color: '#b22c2c',
          border: 'none',
          borderRadius: 4,
          padding: '4px 10px',
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        Exit staff mode
      </button>
    </div>
  );
}

function renderView(view: ViewName, customer: CustomerSummary) {
  switch (view) {
    case 'home':
      return <HomeView customer={customer} />;
    case 'reorder':
      return <QuickReorderView />;
    case 'designs':
      return <MyDesignsView />;
    case 'orders':
      return <OrdersView />;
    case 'kit':
      return <BrandKitView />;
    case 'team':
      return <MyTeamView />;
    case 'help':
      return <HelpView />;
    default:
      return <HomeView customer={customer} />;
  }
}

function BootSplash() {
  return (
    <div className="boot-splash" role="status" aria-live="polite">
      <div className="boot-splash__spinner" aria-hidden="true" />
      <p className="boot-splash__text serif-italic">Opening your studio…</p>
    </div>
  );
}
